@let enabledCount = enabledCount$ | async;
@let totalCount = totalCount$ | async;
@let queuedTotal = queuedTotal$ | async;
@let toggleUserEnabilityTeamIds = toggleUserEnabilityTeamIds$ | async;
@let isOnDuty = isOnDuty$ | async;
@let isClickable = (isOnAttendantConsole$ | async) ?? false;
@let pinnedTeamIds = pinnedTeamIds$ | async;
@let canPinMore = (canPinMore$ | async) ?? false;

<button
  type="button"
  cdkOverlayOrigin
  #servicesTrigger="cdkOverlayOrigin"
  class="header-bar-trigger-button header-bar-services-trigger"
  [class.open]="isOpen$ | async"
  [attr.aria-expanded]="(isOpen$ | async) ?? false"
  (click)="togglePanel()"
>
  <i [nimbusIcon]="ICONS.USERS"></i>
  <span>{{ TranslationKeys.HeaderBarServicesSummary | translate: { enabled: enabledCount, total: totalCount } }}</span>
  @if (queuedTotal) {
    <span class="header-bar-services-trigger-badge">{{ queuedTotal }}</span>
  }
  <i [nimbusIcon]="ICONS.CHEVRON_DOWN"></i>
</button>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="servicesTrigger"
  [cdkConnectedOverlayPositions]="positions"
  [cdkConnectedOverlayOpen]="(isOpen$ | async) ?? false"
  [cdkConnectedOverlayHasBackdrop]="true"
  cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
  (backdropClick)="closePanel()"
  (detach)="closePanel()"
>
  <div class="header-bar-services-panel">
    <div class="header-bar-services-panel-content">
      <div class="header-bar-services-panel-header">
        <span>{{ TranslationKeys.HeaderBarServicesTitle | translate }}</span>
      </div>

      <nimbus-input-search
        class="header-bar-services-panel-search"
        [formControl]="searchControl"
        [placeholder]="TranslationKeys.HeaderBarSearchServices | translate"
      ></nimbus-input-search>

      @if (isEmpty$ | async) {
        <div class="header-bar-services-panel-empty">
          <i [nimbusIcon]="ICONS.SKILL"></i>
          <span class="header-bar-services-panel-empty-title">{{ TranslationKeys.HeaderBarEmptyServicesTitle | translate }}</span>
          <span class="header-bar-services-panel-empty-description">{{
            TranslationKeys.HeaderBarEmptyServicesDescription | translate
          }}</span>
        </div>
      } @else {
        <div class="header-bar-services-panel-list">
          @if (hasTeamsBased$ | async) {
            <div class="header-bar-services-panel-group-header">
              <span>{{ TranslationKeys.HeaderBarTeamsBased | translate }}</span>
              @if (hasEligibleTeams$ | async) {
                <span class="header-bar-services-panel-all">
                  <span class="header-bar-services-panel-all-label">{{ TranslationKeys.HeaderBarAllServices | translate }}</span>
                  @if (isMixed$ | async) {
                    <!-- Material slide toggle has no mixed state; clicking this enables all eligible teams. -->
                    <button
                      type="button"
                      class="header-bar-services-panel-all-toggle-mixed"
                      [disabled]="(isSettingAll$ | async) ?? false"
                      [attr.aria-label]="TranslationKeys.HeaderBarAllServices | translate"
                      (click)="setAll(true)"
                    >
                      <span class="header-bar-services-panel-all-toggle-mixed-knob">
                        <i [nimbusIcon]="ICONS.REMOVE_VALUE"></i>
                      </span>
                    </button>
                  } @else {
                    <nimbus-input-slide-toggle
                      [formControl]="allServicesControl"
                      [isDisabled]="(isSettingAll$ | async) ?? false"
                      [ariaLabel]="TranslationKeys.HeaderBarAllServices | translate"
                      (changeValue)="setAll($event.checked)"
                    ></nimbus-input-slide-toggle>
                  }
                </span>
              }
            </div>
            @for (team of filteredTeamsBased$ | async; track team.teamId) {
              @let isTeamPinned = pinnedTeamIds?.has(team.teamId) ?? false;
              <div class="header-bar-services-panel-row">
                <button
                  type="button"
                  class="header-bar-services-panel-row-pin"
                  [class.pinned]="isTeamPinned"
                  [disabled]="!isTeamPinned && !canPinMore"
                  [attr.aria-label]="
                    (isTeamPinned ? TranslationKeys.HeaderBarUnpinService : TranslationKeys.HeaderBarPinService) | translate
                  "
                  (click)="togglePin(team, $event)"
                >
                  <i [nimbusIcon]="isTeamPinned ? ICONS.STAR : ICONS.STAR_LIGHT"></i>
                  <nimbus-tooltip
                    [text]="
                      (isTeamPinned
                        ? TranslationKeys.HeaderBarUnpinService
                        : canPinMore
                          ? TranslationKeys.HeaderBarPinService
                          : TranslationKeys.HeaderBarPinLimitReached
                      ) | translate
                    "
                  ></nimbus-tooltip>
                </button>
                <nimbus-user-team
                  class="header-bar-services-panel-row-team"
                  [variant]="UserTeamVariant.Row"
                  [team]="team"
                  [clickable]="isClickable"
                  [withCheck]="!toggleUserEnabilityTeamIds?.has(team.teamId)"
                  (teamClick)="onTeamClick(team)"
                ></nimbus-user-team>
              </div>
            }
          }

          @if (hasContactCenter$ | async) {
            @let activeProfile = activeProfile$ | async;
            <div class="header-bar-services-panel-group-header">
              <span>{{ TranslationKeys.HeaderBarContactCenter | translate }}</span>
              <span class="header-bar-services-panel-duty-indicator" [class.on-duty]="isOnDuty">
                <span class="header-bar-services-panel-duty-indicator-label">
                  {{
                    activeProfile?.name ||
                      ((isOnDuty ? TranslationKeys.GeneralDutyProfileDuty : TranslationKeys.GeneralDutyProfileOffDuty) | translate)
                  }}
                  <nimbus-tooltip
                    [text]="
                      activeProfile?.name ||
                      ((isOnDuty ? TranslationKeys.GeneralDutyProfileDuty : TranslationKeys.GeneralDutyProfileOffDuty) | translate)
                    "
                  ></nimbus-tooltip>
                </span>
                <span class="header-bar-services-panel-duty-indicator-dot" aria-hidden="true"></span>
                <nimbus-tooltip
                  [text]="TranslationKeys.HeaderBarContactCenterDescription | translate"
                  [ariaLabel]="TranslationKeys.HeaderBarContactCenterDescription | translate"
                ></nimbus-tooltip>
              </span>
            </div>
            <div class="header-bar-services-panel-group-description">
              {{ TranslationKeys.HeaderBarContactCenterDescription | translate }}
            </div>
            @for (team of filteredContactCenter$ | async; track team.teamId) {
              @let isTeamPinned = pinnedTeamIds?.has(team.teamId) ?? false;
              <div class="header-bar-services-panel-row">
                <button
                  type="button"
                  class="header-bar-services-panel-row-pin"
                  [class.pinned]="isTeamPinned"
                  [disabled]="!isTeamPinned && !canPinMore"
                  [attr.aria-label]="
                    (isTeamPinned ? TranslationKeys.HeaderBarUnpinService : TranslationKeys.HeaderBarPinService) | translate
                  "
                  (click)="togglePin(team, $event)"
                >
                  <i [nimbusIcon]="isTeamPinned ? ICONS.STAR : ICONS.STAR_LIGHT"></i>
                  <nimbus-tooltip
                    [text]="
                      (isTeamPinned
                        ? TranslationKeys.HeaderBarUnpinService
                        : canPinMore
                          ? TranslationKeys.HeaderBarPinService
                          : TranslationKeys.HeaderBarPinLimitReached
                      ) | translate
                    "
                  ></nimbus-tooltip>
                </button>
                <nimbus-user-team
                  class="header-bar-services-panel-row-team"
                  [variant]="UserTeamVariant.Row"
                  [team]="team"
                  [clickable]="isClickable"
                  [withCheck]="!toggleUserEnabilityTeamIds?.has(team.teamId)"
                  (teamClick)="onTeamClick(team)"
                ></nimbus-user-team>
              </div>
            }
          }
        </div>
      }
    </div>
  </div>
</ng-template>
